// Components/NotificationPanel/NotificationPanel.jsx - FULLY FIXED VERSION
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import Apiurl from "../../Environment/Apiurl";
import {
  X,
  Check,
  CheckCheck,
  Trash2,
  Info,
  User,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  Clock,
  Shield,
  Lock,
  FileText,
  Settings,
  Bell,
  Zap,
  Trash,
  Fingerprint,
  CalendarDays,
  Wallet,
  ClipboardList,
  Package,
  ShoppingCart,
  FolderKanban,
  Users,
} from "lucide-react";
import "../../assests/css/NotificationPanel.css";
import "../../assests/css/Toast.css";

// ─── Design tokens - Professional light theme ────────────────────────────────
const C = {
  primary: "#3b82f6",
  primaryLight: "#eff6ff",
  primaryBorder: "#bfdbfe",
  success: "#10b981",
  successLight: "#d1fae5",
  successBorder: "#6ee7b7",
  danger: "#ef4444",
  dangerLight: "#fee2e2",
  dangerBorder: "#fca5a5",
  warning: "#f59e0b",
  warningLight: "#fef3c7",
  warningBorder: "#fde68a",
  warningText: "#92400e",
  text: "#0f172a",
  textSub: "#64748b",
  textMuted: "#94a3b8",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  bg: "#f8fafc",
  bgLight: "#ffffff",
  white: "#ffffff",
  unreadBg: "#eff6ff",
  unreadBorder: "#bfdbfe",
  unreadAccent: "#3b82f6",
  readBorder: "#e2e8f0",
  readAccent: "#cbd5e1",
  shadow: "rgba(15, 23, 42, 0.08)",
};

// ─── UI-only visual classifier ───────────────────────────────────────────────
// This changes presentation only. Notification fetching, polling, read/delete,
// approval/rejection and backend payloads remain untouched.
const getNotificationVisual = ({
  title = "",
  message = "",
  type = "",
  actionBy = null,
} = {}) => {
  const text = `${title} ${message}`.toLowerCase();

  if (/reminder|remainder/.test(text)) {
    return { category: "Reminder", tone: "orange", Icon: Bell };
  }

  if (/lead|enquiry|quotation|site\s*design/.test(text)) {
    return { category: "Leads", tone: "blue", Icon: ClipboardList };
  }

  if (/punch|attendance|break\s*(in|out)?|biometric/.test(text)) {
    return {
      category: "Attendance",
      tone:
        type === "warning" ? "orange" : type === "success" ? "green" : "blue",
      Icon: Fingerprint,
    };
  }
  if (/leave|holiday/.test(text)) {
    return {
      category: /holiday/.test(text) ? "System" : "Leave",
      tone: /holiday/.test(text) ? "red" : "purple",
      Icon: CalendarDays,
    };
  }
  if (/salary|payslip|advance|allowance/.test(text)) {
    return { category: "Salary", tone: "teal", Icon: Wallet };
  }
  if (/task|assign|reassign/.test(text)) {
    return { category: "Tasks", tone: "purple", Icon: ClipboardList };
  }
  if (/inventory|stock|product|purchase/.test(text)) {
    return { category: "Inventory", tone: "blue", Icon: Package };
  }
  if (/order/.test(text)) {
    return { category: "Orders", tone: "orange", Icon: ShoppingCart };
  }
  if (/project/.test(text)) {
    return { category: "Projects", tone: "blue", Icon: FolderKanban };
  }
  if (/client|dealer|distributor|employee|user/.test(text)) {
    return { category: "HR", tone: "green", Icon: Users };
  }

  const isSystemActor =
    String(actionBy?.userId || "").toLowerCase() === "system" ||
    String(actionBy?.userName || "").toLowerCase() === "system" ||
    String(actionBy?.userRole || "").toLowerCase() === "system";
  const category = isSystemActor
    ? "System"
    : actionBy?.userRole || "User Action";
  if (type === "success") return { category, tone: "green", Icon: CheckCircle };
  if (type === "warning")
    return { category, tone: "orange", Icon: AlertTriangle };
  if (type === "error" || type === "alert")
    return { category, tone: "red", Icon: XCircle };
  return { category, tone: "blue", Icon: Bell };
};

// ================= TOAST =================
const Toast = ({
  message,
  type,
  title,
  createdAt,
  actionBy,
  displayBranch,
  onClose,
}) => {
  const visual = getNotificationVisual({ title, message, type, actionBy });
  const ToastIcon = visual.Icon;

  const formatToastTime = (value) => {
    if (!value) return "Just now";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "Just now";
    const diff = Math.max(0, Date.now() - d.getTime());
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  };

  return ReactDOM.createPortal(
    <div className={`custom-toast ${type || "info"} tone-${visual.tone}`}>
      <div className="toast-content">
        <div className={`toast-icon-shell tone-${visual.tone}`}>
          <ToastIcon size={22} className="toast-icon" strokeWidth={2.15} />
        </div>
        <div className="toast-copy">
          {title && <div className="toast-title">{title}</div>}
          <div className="toast-message">{message}</div>
          <div className="toast-meta">
            {actionBy?.userName && (
              <span>
                {actionBy.userName}
                {actionBy.employeeId ? ` (${actionBy.employeeId})` : ""}
              </span>
            )}
            {displayBranch && <span> • {displayBranch}</span>}
            <span> • {formatToastTime(createdAt)}</span>
          </div>
        </div>
        <button
          className="toast-close"
          onClick={onClose}
          aria-label="Close notification"
        >
          <X size={17} />
        </button>
      </div>
    </div>,
    document.body,
  );
};

// ================= CLEAR ALL MODAL =================
const ClearAllModal = ({ isOpen, onConfirm, onCancel, count, loading }) => {
  if (!isOpen) return null;
  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2100,
      }}
    >
      <div
        style={{
          background: C.white,
          borderRadius: "12px",
          padding: "32px 28px",
          maxWidth: "420px",
          width: "90%",
          boxShadow: "0 20px 60px rgba(15, 23, 42, 0.2)",
          border: `1px solid ${C.borderLight}`,
          fontFamily: '"Inter", sans-serif',
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: C.dangerLight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Trash size={22} style={{ color: C.danger }} />
          </div>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "700",
              color: C.text,
            }}
          >
            Clear All Notifications?
          </h3>
        </div>
        <p
          style={{
            margin: "0 0 26px",
            color: C.textSub,
            fontSize: "14px",
            lineHeight: "1.6",
          }}
        >
          You are about to delete{" "}
          <strong>
            {count} notification{count !== 1 ? "s" : ""}
          </strong>
          . This action cannot be undone.
        </p>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1,
              padding: "11px 18px",
              border: `1.5px solid ${C.border}`,
              background: C.white,
              color: C.textSub,
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1,
              padding: "11px 18px",
              border: "none",
              background: C.danger,
              color: C.white,
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
              opacity: loading ? 0.7 : 1,
              transition: "all 0.2s",
            }}
          >
            <Trash size={16} /> {loading ? "Clearing..." : "Clear All"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ================= BELL BADGE (exported for use in header/navbar) =================
export const NotificationBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span
      style={{
        position: "absolute",
        top: -6,
        right: -6,
        minWidth: count > 9 ? 20 : 18,
        height: 18,
        padding: "0 5px",
        background: C.danger,
        color: C.white,
        borderRadius: "100px",
        fontSize: count > 99 ? "9px" : "11px",
        fontWeight: "700",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
        border: `2px solid ${C.white}`,
        boxShadow: "0 2px 8px rgba(239, 68, 68, 0.4)",
        zIndex: 1,
      }}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
};

// ================= MAIN COMPONENT =================
const NotificationPanel = ({ isOpen, onClose, onUnreadCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("today");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "",
    title: "",
    createdAt: null,
    actionBy: null,
    displayBranch: "",
  });
  const [actionLoading, setActionLoading] = useState(null);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [clearAllLoading, setClearAllLoading] = useState(false);

  const [currentUserRole, setCurrentUserRole] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");

  const seenNotificationIdsRef = useRef(new Set());
  const pollingInitializedRef = useRef(false);
  const pollingIntervalRef = useRef(null);
  const isClearingAllRef = useRef(false);

  useEffect(() => {
    const role = localStorage.getItem("role") || "";
    const uid = localStorage.getItem("userId") || "";
    setCurrentUserRole(role);
    setCurrentUserId(uid);
  }, []);

  const showToast = (
    msg,
    type = "success",
    title = "",
    createdAt = null,
    actionBy = null,
    displayBranch = "",
  ) =>
    setToast({
      show: true,
      message: msg,
      type,
      title,
      createdAt,
      actionBy,
      displayBranch,
    });
  const closeToast = () =>
    setToast({
      show: false,
      message: "",
      type: "",
      title: "",
      createdAt: null,
      actionBy: null,
      displayBranch: "",
    });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  // ── Smart unread count: only genuinely unread, excluding already-processed ──
  // Backend is the source of truth for recipient, branch and module-permission scope.
  // Do not re-broadcast role notifications in the browser.
  const isNotificationForCurrentUser = (n) => Boolean(n);

  const visibleNotifications = (list) =>
    (list || []).filter(isNotificationForCurrentUser);
  const getUnreadCount = (notifs) =>
    visibleNotifications(notifs).filter((n) => !n.isRead).length;

  // ── Notify parent of badge count ────────────────────────────────────────
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(getUnreadCount(notifications));
    }
  }, [notifications, onUnreadCountChange]);

  // ================= POLLING =================
  const checkForNewNotifications = async () => {
    // Don't poll while Clear All is mutating the server list.
    if (isClearingAllRef.current) return;

    try {
      const res = await axios.get(`${Apiurl}/notifications`, getAuthHeaders());
      if (!res.data.success) return;

      const serverNotifications = visibleNotifications(
        res.data.notifications || [],
      );

      // First successful poll establishes the baseline. Existing/old items
      // must never reappear as a toast after refresh/login.
      if (!pollingInitializedRef.current) {
        serverNotifications.forEach((n) => {
          if (n?._id) seenNotificationIdsRef.current.add(String(n._id));
        });
        pollingInitializedRef.current = true;
      } else {
        // The API is sorted newest-first. Capture every unseen id so a deleted
        // latest item can never make an older notification toast again. If
        // multiple notifications arrive within one 10s window, toast only the
        // latest one and keep the rest in the bell list.
        const unseen = serverNotifications.filter(
          (n) => n?._id && !seenNotificationIdsRef.current.has(String(n._id)),
        );

        unseen.forEach((n) =>
          seenNotificationIdsRef.current.add(String(n._id)),
        );

        if (unseen.length > 0) {
          const latest = unseen[0];
          showToast(
            latest.message || "New notification",
            latest.type || "info",
            latest.title || "🔔 New",
            latest.createdAt || null,
            latest.actionBy || null,
            latest.displayBranch || "",
          );
        }
      }

      // Keep the open panel live without changing any read/delete/approval logic.
      if (isOpen) setNotifications(serverNotifications);

      if (onUnreadCountChange) {
        onUnreadCountChange(getUnreadCount(serverNotifications));
      }

      if (serverNotifications.length === 0 && isOpen) {
        setNotifications([]);
      }
    } catch (err) {
      // Silent fail for polling
    }
  };

  useEffect(() => {
    checkForNewNotifications();
    pollingIntervalRef.current = setInterval(checkForNewNotifications, 10000);
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isOpen, currentUserId, currentUserRole]);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen]);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  // ================= FETCH =================
  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${Apiurl}/notifications`, getAuthHeaders());
      if (res.data.success) {
        const notifs = visibleNotifications(res.data.notifications || []);
        setNotifications(notifs);
        notifs.forEach((n) => {
          if (n?._id) seenNotificationIdsRef.current.add(String(n._id));
        });
        if (onUnreadCountChange) onUnreadCountChange(getUnreadCount(notifs));
      } else {
        setError("Failed to load notifications");
      }
    } catch (err) {
      setError("Connection error");
    } finally {
      setLoading(false);
    }
  };

  // ================= CLEAR ALL - FIXED =================
  const handleClearAll = async () => {
    setClearAllLoading(true);
    isClearingAllRef.current = true;

    try {
      // Delete all notifications from server
      await Promise.all(
        notifications.map((n) =>
          axios.delete(`${Apiurl}/notifications/${n._id}`, getAuthHeaders()),
        ),
      );

      // Clear local state immediately
      setNotifications([]);
      setShowClearAllModal(false);

      if (onUnreadCountChange) onUnreadCountChange(0);

      showToast(
        `All ${notifications.length} notification${notifications.length !== 1 ? "s" : ""} cleared!`,
        "success",
        "✅ Cleared",
      );

      // Small delay before re-enabling polling to prevent race condition
      setTimeout(() => {
        isClearingAllRef.current = false;
      }, 2000);
    } catch (err) {
      isClearingAllRef.current = false;
      showToast(
        err.response?.data?.message || "Failed to clear notifications",
        "error",
        "❌ Error",
      );
    } finally {
      setClearAllLoading(false);
    }
  };

  // ================= APPROVE =================
  const handleApprove = async (e, notification) => {
    e.stopPropagation();
    setActionLoading(notification._id + "_approve");
    try {
      const meta = notification.metadata || {};

      if (meta.requestType === "password_change") {
        await axios.put(
          `${Apiurl}/user/${meta.targetUserId}/password`,
          { newPassword: meta.newPassword },
          getAuthHeaders(),
        );
      } else if (meta.requestType === "user_edit") {
        await axios.put(
          `${Apiurl}/user/${meta.targetUserId}`,
          meta.updateData,
          getAuthHeaders(),
        );
      }

      // Mark notification as read (processed)
      await axios.put(
        `${Apiurl}/notifications/${notification._id}/read`,
        {},
        getAuthHeaders(),
      );

      // Send response to requester
      if (meta.requestedBy) {
        await axios.post(
          `${Apiurl}/notifications`,
          {
            recipientId: meta.requestedBy,
            recipientRole: "Branch Admin",
            title: "✅ Request Approved",
            message: `Your ${meta.requestType === "password_change" ? "password change" : "user edit"} request for "${meta.targetUserName || "user"}" has been approved`,
            type: "success",
            actionBy: {
              userId: currentUserId,
              userName: "Super Admin",
              userRole: "Super Admin",
            },
            metadata: {
              action: "approval_response",
              requestType: meta.requestType,
              status: "approved",
              requestedBy: meta.requestedBy,
            },
          },
          getAuthHeaders(),
        );
      }

      showToast("Request approved and applied!", "success", "✅ Approved");
      await fetchNotifications();
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to approve",
        "error",
        "❌ Error",
      );
    } finally {
      setActionLoading(null);
    }
  };

  // ================= REJECT =================
  const handleReject = async (e, notification) => {
    e.stopPropagation();
    setActionLoading(notification._id + "_reject");
    try {
      const meta = notification.metadata || {};

      await axios.put(
        `${Apiurl}/notifications/${notification._id}/read`,
        {},
        getAuthHeaders(),
      );

      if (meta.requestedBy) {
        await axios.post(
          `${Apiurl}/notifications`,
          {
            recipientId: meta.requestedBy,
            recipientRole: "Branch Admin",
            title: "❌ Request Rejected",
            message: `Your ${meta.requestType === "password_change" ? "password change" : "user edit"} request for "${meta.targetUserName || "user"}" was rejected`,
            type: "alert",
            actionBy: {
              userId: currentUserId,
              userName: "Super Admin",
              userRole: "Super Admin",
            },
            metadata: {
              action: "approval_response",
              requestType: meta.requestType,
              status: "rejected",
              requestedBy: meta.requestedBy,
            },
          },
          getAuthHeaders(),
        );
      }

      showToast("Request rejected", "success", "✅ Rejected");
      await fetchNotifications();
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to reject",
        "error",
        "❌ Error",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const notificationRoute = (notification) => {
    const type = notification?.metadata?.entityType;
    const routes = {
      lead: "/leads/enquiry-list",
      siteDesign: "/leads/site-designer",
      inventory_stock: "/inventory/stock",
      inventory_order: "/inventory/orders",
      inventory_product: "/inventory/product",
      inventory_purchase: "/inventory/purchase",
      inventory_damage: "/inventory/damage",
      attendance: "/hr/attendances",
      employee: "/hr/employee-master",
      task: "/hr/task-management",
      carpenter: "/craftsmen/craftsmen-master",
      carpenterAttendance: "/craftsmen/attendances",
      carpenterAdvance: "/craftsmen/carpenteradvance",
      job: "/craftsmen/job-assigned",
      project: "/projects",
      reminder: "/master/reminder",
    };
    const base = notification?.metadata?.route || routes[type] || "";
    const entityId = notification?.metadata?.entityId;
    if (!base) return "";
    if (notification?.metadata?.route) return base;
    if (type === "lead" && entityId) return `${base}?notificationId=${encodeURIComponent(entityId)}`;
    if (type === "project" && entityId) return `${base}?notificationId=${encodeURIComponent(entityId)}`;
    return base;
  };

  const openNotification = async (notification, isPendingReq) => {
    if (!notification) return;
    if (!notification.isRead && !isPendingReq)
      await markAsRead(notification._id);
    if (isPendingReq) return;
    const route = notificationRoute(notification);
    if (route) window.location.assign(route);
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(
        `${Apiurl}/notifications/${id}/read`,
        {},
        getAuthHeaders(),
      );
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );
    } catch (err) {
      // Silent fail
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${Apiurl}/notifications/mark-all-read`,
        {},
        getAuthHeaders(),
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      if (onUnreadCountChange) onUnreadCountChange(0);
    } catch (err) {
      // Silent fail
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${Apiurl}/notifications/${id}`, getAuthHeaders());
      setNotifications((prev) => {
        const next = prev.filter((n) => n._id !== id);
        if (onUnreadCountChange) onUnreadCountChange(getUnreadCount(next));
        return next;
      });
    } catch (err) {
      // Silent fail
    }
  };

  const getIconWithColor = (type) => {
    const p = { size: 18, strokeWidth: 2 };
    switch (type) {
      case "user":
        return <User {...p} style={{ color: C.primary }} />;
      case "alert":
        return <AlertTriangle {...p} style={{ color: C.warning }} />;
      case "success":
        return <CheckCircle {...p} style={{ color: C.success }} />;
      case "warning":
        return <AlertTriangle {...p} style={{ color: "#eab308" }} />;
      default:
        return <Info {...p} style={{ color: C.textSub }} />;
    }
  };

  const formatTime = (d) => {
    if (!d) return "";
    try {
      const value = new Date(d);
      if (Number.isNaN(value.getTime())) return "";
      return value.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };

  // ── Approval response notification (sent back to requester) ─────────────
  const isApprovalResponse = (n) => n?.metadata?.action === "approval_response";

  // ── Reminder detection/grouping ─────────────────────────────────────────
  // Supports both the correct "reminder" spelling and older "remainder" data.
  const isReminderNotification = (n = {}) => {
    const meta = n.metadata || {};
    const haystack = [
      n.title,
      n.message,
      n.module,
      n.category,
      meta.module,
      meta.type,
      meta.action,
      meta.requestType,
      meta.reminderType,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return /reminder|remainder/.test(haystack);
  };

  const getReminderGroupKey = (n = {}) => {
    const meta = n.metadata || {};
    return String(
      meta.reminderId ||
        meta.remainderId ||
        meta.referenceId ||
        meta.reminderTitle ||
        meta.remainderTitle ||
        n.title ||
        "Reminder",
    );
  };

  const reminderCount = notifications.filter(isReminderNotification).length;

  // ── filtered list ────────────────────────────────────────────────────────
  const matchesDateFilter = (n) => {
    if (dateFilter === "all") return true;
    const created = new Date(n.createdAt);
    if (Number.isNaN(created.getTime())) return false;
    const now = new Date();
    const startOfDay = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const today = startOfDay(now);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dayBefore = new Date(today);
    dayBefore.setDate(dayBefore.getDate() - 2);
    if (dateFilter === "today") return created >= today && created < tomorrow;
    if (dateFilter === "yesterday")
      return created >= yesterday && created < today;
    if (dateFilter === "dayBefore")
      return created >= dayBefore && created < yesterday;
    if (dateFilter === "week") {
      const weekStart = new Date(today);
      const day = weekStart.getDay();
      weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1));
      return created >= weekStart && created < tomorrow;
    }
    if (dateFilter === "month") {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return created >= monthStart && created < nextMonth;
    }
    if (dateFilter === "custom") {
      if (!customStartDate && !customEndDate) return true;
      const start = customStartDate
        ? new Date(`${customStartDate}T00:00:00`)
        : null;
      const end = customEndDate
        ? new Date(`${customEndDate}T23:59:59.999`)
        : null;
      return (!start || created >= start) && (!end || created <= end);
    }
    return true;
  };

  const filteredList = notifications.filter((n) => {
    const statusMatch =
      filter === "unread"
        ? !n.isRead
        : filter === "read"
          ? n.isRead
          : filter === "reminder"
            ? isReminderNotification(n)
            : true;
    return statusMatch && matchesDateFilter(n);
  });

  // In the Reminder tab keep identical reminder events together, newest first.
  const displayedList =
    filter === "reminder"
      ? [...filteredList].sort((a, b) => {
          const ga = getReminderGroupKey(a);
          const gb = getReminderGroupKey(b);
          if (ga !== gb) return ga.localeCompare(gb);
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        })
      : filteredList;

  const unreadCount = getUnreadCount(notifications);
  const readCount = notifications.length - unreadCount;

  // ================= APPROVAL SECTION =================
  const renderApprovalSection = (notification) => {
    const meta = notification.metadata || {};
    const isRead = notification.isRead;
    const isSuperAdmin = currentUserRole === "Super Admin";

    // Super Admin sees pending approve/reject buttons (only if not yet processed)
    if (
      isSuperAdmin &&
      meta.action === "approval_request" &&
      meta.status === "pending" &&
      !isRead
    ) {
      const busy =
        actionLoading === notification._id + "_approve" ||
        actionLoading === notification._id + "_reject";
      const isPwd = meta.requestType === "password_change";

      return (
        <div
          style={{
            marginTop: 12,
            padding: "12px 14px",
            backgroundColor: C.warningLight,
            border: `1px solid ${C.warningBorder}`,
            borderLeft: `3px solid ${C.warning}`,
            borderRadius: "8px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginBottom: 10,
            }}
          >
            {isPwd ? (
              <Lock size={15} style={{ color: C.warning }} />
            ) : (
              <FileText size={15} style={{ color: C.warning }} />
            )}
            <span
              style={{
                fontSize: "13px",
                fontWeight: "700",
                color: C.warningText,
              }}
            >
              {isPwd ? "🔐 Password Change Request" : "✏️ User Edit Request"}
            </span>
          </div>

          {meta.targetUserName && (
            <div
              style={{ fontSize: "13px", color: C.textSub, marginBottom: 8 }}
            >
              <span style={{ fontWeight: "600" }}>User:</span>{" "}
              {meta.targetUserName}
            </div>
          )}

          {meta.requestType === "user_edit" &&
            Array.isArray(meta.changes) &&
            meta.changes.length > 0 && (
              <div
                style={{ fontSize: "12px", color: C.textSub, marginBottom: 10 }}
              >
                {meta.changes.map((c, i) => (
                  <div key={i} style={{ marginBottom: 3 }}>
                    • {c}
                  </div>
                ))}
              </div>
            )}

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button
              onClick={(e) => handleApprove(e, notification)}
              disabled={busy}
              style={{
                flex: 1,
                padding: "9px 12px",
                background: busy ? "#86efac" : C.success,
                color: C.white,
                border: "none",
                borderRadius: "7px",
                fontSize: "13px",
                fontWeight: "700",
                cursor: busy ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                transition: "all 0.2s",
              }}
            >
              {actionLoading === notification._id + "_approve" ? (
                <>⟳ Approving…</>
              ) : (
                <>
                  <ThumbsUp size={14} /> Approve
                </>
              )}
            </button>
            <button
              onClick={(e) => handleReject(e, notification)}
              disabled={busy}
              style={{
                flex: 1,
                padding: "9px 12px",
                background: busy ? "#fca5a5" : C.danger,
                color: C.white,
                border: "none",
                borderRadius: "7px",
                fontSize: "13px",
                fontWeight: "700",
                cursor: busy ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                transition: "all 0.2s",
              }}
            >
              {actionLoading === notification._id + "_reject" ? (
                <>⟳ Rejecting…</>
              ) : (
                <>
                  <ThumbsDown size={14} /> Reject
                </>
              )}
            </button>
          </div>
        </div>
      );
    }

    // Already processed approval request — show compact badge
    if (isSuperAdmin && meta.action === "approval_request" && isRead) {
      return (
        <div
          style={{
            marginTop: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 12px",
            background: "#f1f5f9",
            border: "1px solid #e2e8f0",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "600",
            color: C.textMuted,
          }}
        >
          <Check size={12} /> Processed
        </div>
      );
    }

    // Approval response (for Branch Admin)
    if (isApprovalResponse(notification)) {
      const ok = meta.status === "approved";
      return (
        <div
          style={{
            marginTop: 10,
            padding: "8px 14px",
            background: ok ? C.successLight : C.dangerLight,
            border: `1px solid ${ok ? C.successBorder : C.dangerBorder}`,
            borderLeft: `3px solid ${ok ? C.success : C.danger}`,
            borderRadius: "7px",
            fontSize: "13px",
            fontWeight: "700",
            color: ok ? "#059669" : "#dc2626",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          {ok ? <CheckCircle size={15} /> : <XCircle size={15} />}
          {ok ? "Approved by Super Admin" : "Rejected by Super Admin"}
        </div>
      );
    }

    return null;
  };

  // ================= RENDER =================
  return (
    <>
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          title={toast.title}
          createdAt={toast.createdAt}
          actionBy={toast.actionBy}
          displayBranch={toast.displayBranch}
          onClose={closeToast}
        />
      )}

      <ClearAllModal
        isOpen={showClearAllModal}
        onConfirm={handleClearAll}
        onCancel={() => setShowClearAllModal(false)}
        count={notifications.length}
        loading={clearAllLoading}
      />

      {/* Overlay */}
      <div
        className={`notification-overlay ${isOpen ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div className={`notification-panel ${isOpen ? "open" : ""}`}>
        {/* ── HEADER ── */}
        <div
          className="notification-panel-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: `1px solid ${C.border}`,
            background: C.bgLight,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ position: "relative", display: "inline-flex" }}>
              <Bell size={24} style={{ color: C.primary, strokeWidth: 2.5 }} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -8,
                    minWidth: 18,
                    height: 18,
                    padding: "0 4px",
                    background: C.danger,
                    color: C.white,
                    borderRadius: "100px",
                    fontSize: unreadCount > 99 ? "9px" : "10px",
                    fontWeight: "800",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `2px solid ${C.bgLight}`,
                    lineHeight: 1,
                    boxShadow: "0 2px 6px rgba(239, 68, 68, 0.35)",
                  }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
            <h3
              style={{
                margin: 0,
                fontSize: "17px",
                fontWeight: "700",
                color: C.text,
              }}
            >
              Notifications
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              color: C.textSub,
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = C.borderLight)
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <X size={20} />
          </button>
        </div>

        {/* ── FILTER TABS + ACTIONS ── */}
        <div
          className="notification-filter-row"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            borderBottom: `1px solid ${C.border}`,
            background: C.bg,
            flexWrap: "nowrap",
            rowGap: 0,
            overflowX: "auto",
            overflowY: "visible",
          }}
        >
          {[
            { key: "all", label: `All`, count: notifications.length },
            { key: "unread", label: `Unread`, count: unreadCount },
            { key: "read", label: `Read`, count: readCount },
            { key: "reminder", label: `Reminders`, count: reminderCount },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: "7px 14px",
                fontSize: "13px",
                fontWeight: "600",
                border: "none",
                borderRadius: "20px",
                cursor: "pointer",
                background: filter === key ? C.primary : C.borderLight,
                color: filter === key ? C.white : C.textSub,
                transition: "all 0.2s",
              }}
            >
              {label}
              {count > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    padding: "2px 7px",
                    background:
                      filter === key ? "rgba(255,255,255,0.3)" : "#94a3b8",
                    color: C.white,
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "700",
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          ))}

          <div
            style={{
              width: "auto",
              display: "flex",
              gap: 7,
              alignItems: "center",
              flexWrap: "nowrap",
              paddingTop: 0,
              marginLeft: 4,
              flexShrink: 0,
            }}
          >
            <CalendarDays size={15} color={C.textSub} />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                padding: "7px 10px",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                background: C.white,
                color: C.text,
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="dayBefore">Day Before Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
              <option value="all">All Time</option>
            </select>
            {dateFilter === "custom" && (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    padding: "6px 8px",
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <span style={{ color: C.textMuted, fontSize: 12 }}>to</span>
                <input
                  type="date"
                  value={customEndDate}
                  min={customStartDate || undefined}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    padding: "6px 8px",
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
              </>
            )}
          </div>

          <div
            className="notification-toolbar-actions"
            style={{
              width: "auto",
              marginLeft: "auto",
              display: "flex",
              gap: 6,
              justifyContent: "flex-end",
              flexWrap: "nowrap",
              flexShrink: 0,
            }}
          >
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  padding: "7px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  border: "none",
                  borderRadius: "7px",
                  cursor: "pointer",
                  background: "#dbeafe",
                  color: "#0284c7",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#bfdbfe")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#dbeafe")
                }
              >
                <CheckCheck size={15} /> Mark all read
              </button>
            )}

            {notifications.length > 0 && (
              <button
                onClick={() => setShowClearAllModal(true)}
                style={{
                  padding: "7px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  border: "none",
                  borderRadius: "7px",
                  cursor: "pointer",
                  background: C.dangerLight,
                  color: C.danger,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#fecaca")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = C.dangerLight)
                }
              >
                <Trash size={15} /> Clear all
              </button>
            )}
          </div>
        </div>

        {/* ── LIST ── */}
        <div
          className="notification-list"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px",
            background: C.bg,
          }}
        >
          {loading ? (
            <div
              style={{
                padding: "60px 20px",
                textAlign: "center",
                color: C.textMuted,
                fontSize: "14px",
              }}
            >
              <div style={{ marginBottom: 12, opacity: 0.3 }}>
                <Bell size={36} />
              </div>
              Loading notifications…
            </div>
          ) : error ? (
            <div
              style={{
                margin: 14,
                padding: 16,
                background: C.dangerLight,
                color: "#991b1b",
                borderRadius: "10px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <AlertCircle size={18} /> {error}
            </div>
          ) : displayedList.length === 0 ? (
            <div
              style={{
                padding: "60px 20px",
                textAlign: "center",
                color: C.textMuted,
                fontSize: "14px",
              }}
            >
              <Bell size={36} style={{ opacity: 0.25, marginBottom: 12 }} />
              <div>
                {filter === "unread"
                  ? "No unread notifications"
                  : filter === "read"
                    ? "No read notifications"
                    : filter === "reminder"
                      ? "No reminder notifications"
                      : "No notifications yet"}
              </div>
            </div>
          ) : (
            displayedList.map((notification, index) => {
              const visual = getNotificationVisual(notification);
              const reminderGroupKey = getReminderGroupKey(notification);
              const previousReminderGroupKey =
                index > 0
                  ? getReminderGroupKey(displayedList[index - 1])
                  : null;
              const showReminderGroupHeader =
                filter === "reminder" &&
                reminderGroupKey !== previousReminderGroupKey;
              const reminderGroupCount =
                filter === "reminder"
                  ? displayedList.filter(
                      (n) => getReminderGroupKey(n) === reminderGroupKey,
                    ).length
                  : 0;
              const VisualIcon = visual.Icon;
              const isUnread = !notification.isRead;
              const isPendingReq =
                currentUserRole === "Super Admin" &&
                notification.metadata?.action === "approval_request" &&
                notification.metadata?.status === "pending" &&
                isUnread;

              return (
                <React.Fragment key={notification._id}>
                  {showReminderGroupHeader && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        margin: "10px 2px 8px",
                        padding: "9px 12px",
                        background: "#fff7ed",
                        border: "1px solid #fed7aa",
                        borderRadius: "9px",
                        color: "#9a3412",
                        fontSize: "13px",
                        fontWeight: "800",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                        }}
                      >
                        <Bell size={15} /> {reminderGroupKey}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          background: "#ffedd5",
                          padding: "3px 8px",
                          borderRadius: 20,
                        }}
                      >
                        {reminderGroupCount}
                      </span>
                    </div>
                  )}
                  <div
                    className={`notification-card tone-${visual.tone} ${isUnread ? "is-unread" : "is-read"}`}
                    style={{
                      padding: "14px 16px",
                      marginBottom: "10px",
                      background: isUnread ? C.unreadBg : C.bgLight,
                      border: `1px solid ${isUnread ? C.unreadBorder : C.readBorder}`,
                      borderLeft: `4px solid ${isUnread ? C.unreadAccent : C.readAccent}`,
                      borderRadius: "10px",
                      cursor: isUnread && !isPendingReq ? "pointer" : "default",
                      transition: "all 0.2s",
                      opacity:
                        notification.isRead &&
                        notification.metadata?.action === "approval_request"
                          ? 0.7
                          : 1,
                      boxShadow: isUnread ? `0 2px 8px ${C.shadow}` : "none",
                    }}
                    onClick={() => openNotification(notification, isPendingReq)}
                    onMouseEnter={(e) => {
                      if (isUnread && !isPendingReq)
                        e.currentTarget.style.background = "#e0f2fe";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isUnread
                        ? C.unreadBg
                        : C.bgLight;
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Icon */}
                      <div
                        className={`notification-visual-icon tone-${visual.tone}`}
                      >
                        <VisualIcon size={20} strokeWidth={2.1} />
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "700",
                            color: C.text,
                            marginBottom: 4,
                            display: "flex",
                            alignItems: "center",
                            gap: 9,
                          }}
                        >
                          {notification.title || "Notification"}
                          {isUnread && (
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: C.primary,
                                flexShrink: 0,
                                display: "inline-block",
                              }}
                            />
                          )}
                        </div>

                        <div
                          style={{
                            fontSize: "13px",
                            color: C.textSub,
                            lineHeight: "1.5",
                            marginBottom: 7,
                          }}
                        >
                          {notification.message || "No message"}
                        </div>

                        <div
                          className={`notification-category tone-${visual.tone}`}
                        >
                          {visual.category}
                        </div>

                        <div
                          className="notification-meta-row"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 4,
                            fontSize: "13px",
                            color: C.text,
                            fontWeight: "700",
                            marginBottom: 5,
                          }}
                        >
                          {notification.displayBranch && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <FolderKanban size={12} />
                              <span>
                                <strong>Branch:</strong>{" "}
                                {notification.displayBranch}
                              </span>
                            </div>
                          )}
                          {notification.displayFor && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                fontWeight: "800",
                                color: C.text,
                              }}
                            >
                              <Users size={13} />
                              <span><strong>For:</strong> {notification.displayFor}</span>
                            </div>
                          )}
                          {notification.actionBy?.userName && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                fontWeight: "800",
                                color: C.text,
                              }}
                            >
                              <User size={13} />
                              <span>
                                <strong>By:</strong>{" "}
                                {notification.actionBy.userName}
                                {notification.actionBy.employeeId
                                  ? ` (${notification.actionBy.employeeId})`
                                  : ""}
                              </span>
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              fontWeight: "800",
                              color: C.text,
                            }}
                          >
                            <Clock size={13} />
                            <span>{formatTime(notification.createdAt)}</span>
                          </div>
                        </div>

                        {renderApprovalSection(notification)}
                      </div>

                      {/* Actions */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 5,
                          flexShrink: 0,
                        }}
                      >
                        {isUnread && !isPendingReq && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification._id);
                            }}
                            title="Mark as read"
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "5px",
                              color: C.textSub,
                              borderRadius: "6px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = C.borderLight)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "none")
                            }
                          >
                            <Check size={16} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification._id);
                          }}
                          title="Delete"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "5px",
                            color: C.danger,
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = C.dangerLight)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "none")
                          }
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>

      <style>{`
        .notification-overlay {
          position: fixed; inset: 0;
          background: transparent;
          opacity: 0; pointer-events: none;
          transition: opacity 0.18s ease;
          z-index: 999;
        }
        .notification-overlay.open {
          opacity: 1; pointer-events: auto;
        }
        .notification-panel {
          position: fixed; top: 76px; right: 18px; bottom: 18px;
          width: min(540px, calc(100vw - 36px));
          height: auto;
          max-height: none;
          background: ${C.bgLight};
          border: 1px solid #e7edf5;
          border-radius: 18px;
          box-shadow: 0 24px 70px rgba(15, 23, 42, 0.18), 0 4px 16px rgba(15, 23, 42, 0.06);
          transform: translateY(-12px) scale(0.985);
          transform-origin: top right;
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: transform 0.22s ease, opacity 0.18s ease, visibility 0.18s ease;
          z-index: 1000;
          display: flex; flex-direction: column;
          overflow: hidden;
        }
        .notification-filter-row > button {
          flex: 0 0 auto;
          white-space: nowrap;
        }
        .notification-toolbar-actions > button {
          flex: 0 0 auto;
          white-space: nowrap;
        }
        .notification-panel.open {
          transform: translateY(0) scale(1);
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
        }
        @media (max-width: 560px) {
          .notification-panel {
            top: 68px;
            left: 10px;
            right: 10px;
            bottom: 10px;
            width: auto;
            height: auto;
            max-height: none;
            border-radius: 16px;
          }
        }
      `}</style>
    </>
  );
};

export default NotificationPanel;
